import {Reducer, useReducer} from 'react';

import {Players, Player} from '../types';

export type State = {
  formation: Formation;
  overallCost: number;
  error: string;
  numberOfPlayers: number;
  players: Players;
  substitutes: [Player | null, Player | null, Player | null, Player | null];
  indicatorPosition: {
    position: Player['position'];
    index: number;
    substitute?: boolean;
  };
};

export type Action =
  | {
      type: 'add';
      player: Player;
    }
  | {
      type: 'remove';
      player: Player;
      isSubstitute?: boolean;
    }
  | {
      type: 'select/captain';
      player: Player;
      isSubstitute?: boolean;
    }
  | {
      type: 'setIndicatorPosition';
      indicatorPosition: State['indicatorPosition'];
    }
  | {
      type: 'changeFormation';
      formation: Formation;
    }
  | {
      type: 'reset/error';
    }
  | {
      type: 'set';
      state: State;
    };

type PlayersReducer = Reducer<State, Action>;

function add(prevState: State, action: Action & {type: 'add'}): State {
  const position = action.player.position;
  const positionIndex = getIndexFromPosition(position);
  const index = prevState.players[positionIndex].findIndex((player) => player?.id === action.player.id);
  if (index !== -1) return {...prevState, error: 'Player is already added'};
  const playerToBeReplaced: Player | null = prevState.indicatorPosition.substitute
    ? prevState.substitutes[prevState.indicatorPosition.index]
    : prevState.players[positionIndex][prevState.indicatorPosition.index];
  let overallCost;
  let numberOfPlayers;
  if (playerToBeReplaced) {
    overallCost = prevState.overallCost - playerToBeReplaced.price + action.player.price;
    numberOfPlayers = prevState.numberOfPlayers;
  } else {
    overallCost = prevState.overallCost + action.player.price;
    numberOfPlayers = prevState.numberOfPlayers + 1;
  }

  const indicatorPosition = getNextIndicatorPosition(prevState.formation, prevState.indicatorPosition);

  return {
    ...prevState,
    overallCost,
    numberOfPlayers,
    indicatorPosition,
    players: prevState.indicatorPosition.substitute
      ? prevState.players
      : (prevState.players.map((playersArr, idx) =>
          idx === positionIndex
            ? [
                ...playersArr.slice(0, prevState.indicatorPosition.index),
                action.player,
                ...playersArr.slice(prevState.indicatorPosition.index + 1),
              ]
            : playersArr,
        ) as State['players']),
    substitutes: prevState.indicatorPosition.substitute
      ? ([
          ...prevState.substitutes.slice(0, getIndexFromPosition(action.player.position)),
          action.player,
          ...prevState.substitutes.slice(getIndexFromPosition(action.player.position) + 1),
        ] as State['substitutes'])
      : prevState.substitutes,
  };
}

function remove(prevState: State, action: Action & {type: 'remove'}): State {
  const positionIndex = getIndexFromPosition(action.player.position);
  if (action.isSubstitute)
    return {
      ...prevState,
      numberOfPlayers: prevState.numberOfPlayers - 1,
      overallCost: prevState.overallCost - action.player.price,
      substitutes: [
        ...prevState.substitutes.slice(0, positionIndex),
        null,
        ...prevState.substitutes.slice(positionIndex + 1),
      ] as State['substitutes'],
      indicatorPosition: {
        position: action.player.position,
        substitute: true,
        index: positionIndex,
      },
    };
  const playerToBeRemovedIndex = prevState.players[positionIndex].findIndex(
    (linePlayer) => linePlayer?.id === action.player.id,
  );
  return {
    ...prevState,
    numberOfPlayers: prevState.numberOfPlayers - 1,
    overallCost: prevState.overallCost - action.player.price,
    players: [
      ...prevState.players.slice(0, positionIndex),
      [
        ...prevState.players[positionIndex].slice(0, playerToBeRemovedIndex),
        null,
        ...prevState.players[positionIndex].slice(playerToBeRemovedIndex + 1),
      ],
      ...prevState.players.slice(positionIndex + 1),
    ] as Players,
    indicatorPosition: {position: action.player.position, index: playerToBeRemovedIndex},
  };
}

function selectCaptain(prevState: State, action: Action & {type: 'select/captain'}): State {
  const positionIndex = getIndexFromPosition(action.player.position);
  // 1. Remove prev captain if exists
  const subsWithoutCaptain = prevState.substitutes.map((sub) =>
    sub ? {...sub, is_captain: 0} : null,
  ) as State['substitutes'];
  const playersWithoutCaptain = prevState.players.map((line) =>
    line.map((player) => (player ? {...player, is_captain: 0} : null)),
  ) as State['players'];

  // 2. Mark new captain
  if (action.isSubstitute)
    return {
      ...prevState,
      players: playersWithoutCaptain,
      substitutes: [
        ...subsWithoutCaptain.slice(0, positionIndex),
        {...action.player, is_captain: 1},
        ...subsWithoutCaptain.slice(positionIndex + 1),
      ] as State['substitutes'],
      indicatorPosition: {
        position: action.player.position,
        substitute: true,
        index: positionIndex,
      },
    };
  const playerToBeCaptainIndex = prevState.players[positionIndex].findIndex(
    (linePlayer) => linePlayer?.id === action.player.id,
  );
  return {
    ...prevState,
    substitutes: subsWithoutCaptain,
    players: [
      ...playersWithoutCaptain.slice(0, positionIndex),
      [
        ...playersWithoutCaptain[positionIndex].slice(0, playerToBeCaptainIndex),
        {...action.player, is_captain: 1},
        ...playersWithoutCaptain[positionIndex].slice(playerToBeCaptainIndex + 1),
      ],
      ...playersWithoutCaptain.slice(positionIndex + 1),
    ] as Players,
    indicatorPosition: {position: action.player.position, index: playerToBeCaptainIndex},
  };
}

function changeFormation(prevState: State, action: Action & {type: 'changeFormation'}): State {
  const newPlayers = makePlayers(action.formation, prevState.players);
  const {newCost, newNumberOfPlayers} = [...newPlayers, prevState.substitutes]
    .flatMap((row) => row)
    .reduce(
      (prev, curr) => ({
        newCost: prev.newCost + (curr ? curr?.price : 0),
        newNumberOfPlayers: prev.newNumberOfPlayers + (curr ? 1 : 0),
      }),
      {newCost: 0, newNumberOfPlayers: 0},
    );
  return {
    ...prevState,
    formation: action.formation,
    players: newPlayers,
    overallCost: newCost,
    numberOfPlayers: newNumberOfPlayers,
  };
}

const initialState: State = {
  overallCost: 0,
  players: [[null], [null, null, null, null], [null, null, null, null], [null, null]],
  substitutes: [null, null, null, null],
  formation: '4-4-2',
  numberOfPlayers: 0,
  indicatorPosition: {position: 'Goalkeeper', index: 0},
  error: '',
};

const reducer: PlayersReducer = (prev, action) => {
  switch (action.type) {
    case 'add':
      return add(prev, action);
    case 'remove':
      return remove(prev, action);
    case 'select/captain':
      return selectCaptain(prev, action);
    case 'setIndicatorPosition':
      return {...prev, indicatorPosition: action.indicatorPosition};
    case 'changeFormation':
      return changeFormation(prev, action);
    case 'reset/error':
      return {...prev, error: ''};
    case 'set':
      return action.state;
  }
};

export function usePlayersReducer() {
  return useReducer<PlayersReducer>(reducer, initialState);
}

export function getIndexFromPosition(position: Player['position']) {
  switch (position) {
    case 'Goalkeeper':
      return 0;
    case 'Defender':
      return 1;
    case 'Midfielder':
      return 2;
    case 'Attacker':
      return 3;
  }
}

export function getPositionFromIndex(index: 0 | 1 | 2 | 3): Player['position'] {
  switch (index) {
    case 0:
      return 'Goalkeeper';
    case 1:
      return 'Defender';
    case 2:
      return 'Midfielder';
    case 3:
      return 'Attacker';
  }
}

function makePlayers(formation: Formation, prevPlayers: Players) {
  const gk = prevPlayers[0];
  const defmidatt = formation
    .split('-')
    .map(
      (row, rowIndex) =>
        Array.from({length: Number(row)}).map(
          (_, playerIndex) => prevPlayers[rowIndex + 1][playerIndex] ?? null,
        ) as Player[],
    );
  return [gk, ...defmidatt] as [(Player | null)[], (Player | null)[], (Player | null)[], (Player | null)[]];
}

function getNextIndicatorPosition(
  formation: string,
  indicatorPosition: State['indicatorPosition'],
): State['indicatorPosition'] {
  const noOfPlayers = [1, ...formation.split('-').map(Number)];
  const currentLineIndex = getIndexFromPosition(indicatorPosition.position);
  if (indicatorPosition.substitute)
    if (indicatorPosition.position === 'Attacker')
      return {
        position: 'Goalkeeper',
        index: 0,
        substitute: false,
      };
    else
      return {
        position: getPositionFromIndex((indicatorPosition.index + 1) as 0 | 1 | 2 | 3),
        substitute: true,
        index: indicatorPosition.index + 1,
      };
  if (noOfPlayers[currentLineIndex] > indicatorPosition.index + 1)
    return {
      ...indicatorPosition,
      index: indicatorPosition.index + 1,
    };
  if (noOfPlayers[currentLineIndex] - 1 === indicatorPosition.index && indicatorPosition.position === 'Attacker')
    return {
      index: 0,
      position: 'Goalkeeper',
      substitute: true,
    };

  return {
    index: 0,
    position: getPositionFromIndex((currentLineIndex + 1) as 0 | 1 | 2 | 3),
    substitute: false,
  };
}
